import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useNavigation, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import AutoScheduleModal from '@/components/Tasks/AutoScheduleModal';
import { useAutoScheduleAccess } from '@/components/Tasks/autoScheduleModel';
import CoverageBoard, { getActiveAssigneeCount, getCoverageNeed, getTaskInstanceDate, hasCoverageGap } from '@/components/Tasks/CoverageBoard';
import CutCard from '@/components/General/CutCard';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SolidButton from '@/components/General/SolidButton';
import TaskCard from '@/components/Tasks/TaskCard';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';

const AttentionItem = ({ icon, value, label, tone }) => (
    <View style={styles.attentionItem}>
        <View style={styles.attentionIcon(tone)}><Unicon name={icon} size={16} colorVariant={tone} /></View>
        <Text style={styles.attentionValue}>{value}</Text>
        <Text style={styles.attentionLabel}>{label}</Text>
    </View>
);

const SectionHeader = ({ eyebrow, title, actionLabel, onAction, testID, extraAction = null }) => (
    <View style={styles.sectionHeaderWrap}>
        <View style={styles.sectionHeader}>
            <View style={styles.sectionHeading}>
                {!!eyebrow && <Text style={styles.eyebrow}>{eyebrow}</Text>}
                <Text style={styles.sectionTitle}>{title}</Text>
            </View>
            {!!extraAction && (
                <TouchableOpacity
                    testID={extraAction.testID}
                    accessibilityRole="button"
                    accessibilityLabel={extraAction.disabled && extraAction.hint ? `${extraAction.label}: ${extraAction.hint}` : extraAction.label}
                    accessibilityState={{ disabled: !!extraAction.disabled }}
                    onPress={extraAction.disabled ? undefined : extraAction.onPress}
                    disabled={!!extraAction.disabled}
                    style={styles.sectionAction}
                >
                    <Text style={[styles.sectionActionText, extraAction.disabled && styles.sectionActionTextDisabled]}>{extraAction.label}</Text>
                    <Unicon name={extraAction.icon || "sparkles"} size={14} colorVariant={extraAction.disabled ? "textSecondary" : "tasks"} />
                </TouchableOpacity>
            )}
            {!!onAction && (
                <TouchableOpacity testID={testID} accessibilityRole="button" accessibilityLabel={`${actionLabel}: ${title}`} onPress={onAction} style={styles.sectionAction}>
                    <Text style={styles.sectionActionText}>{actionLabel}</Text>
                    <Unicon name="arrow-forward" size={14} colorVariant="tasks" />
                </TouchableOpacity>
            )}
        </View>
        {!!extraAction?.disabled && !!extraAction?.hint && (
            <Text style={styles.sectionActionHint} accessibilityLiveRegion="polite">{extraAction.hint}</Text>
        )}
    </View>
);

const TeamPreviewCard = ({ team, isTeamAssigner }) => {
    const router = useRouter();
    return (
        <CutCard
            testID={`MyTeam-${team.id}`}
            accessibilityLabel={`Open ${team.name} team`}
            onPress={() => router.push(`/tasks/teams/${team.id}`)}
            style={styles.teamCard}
            contentStyle={styles.teamCardContent}
        >
            <View style={styles.teamIcon}><Unicon name="people-outline" size={19} colorVariant="tasks" /></View>
            <View style={styles.teamCopy}>
                <Text style={styles.teamName}>{team.name}</Text>
                <Text style={styles.teamMeta}>{team.Tasks?.length || 0} task{team.Tasks?.length === 1 ? '' : 's'}{team.Location?.name ? ` · ${team.Location.name}` : ''}</Text>
            </View>
            <View style={styles.teamRoleBadge(isTeamAssigner)}>
                <Unicon name={isTeamAssigner ? "key-outline" : "person-outline"} size={12} colorVariant={isTeamAssigner ? "tasks" : "textSecondary"} />
                <Text style={styles.teamRoleBadgeText(isTeamAssigner)}>{isTeamAssigner ? 'Assigner' : 'Member'}</Text>
            </View>
            <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
        </CutCard>
    );
};

export default function TaskDashboardScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { width } = useWindowDimensions();
    const desktop = width >= 900;
    const { data: loggedInUser, isLoading: userLoading } = Query.people.loggedInUser();
    const { data: personalTasks = [], isLoading: personalLoading, isFetching: personalFetching } = Query.tasks.upcomingTasks({
        personID: loggedInUser?.id,
        horizonDays: 30,
        options: { enabled: !!loggedInUser?.id },
    });
    const { data: teams = [], isLoading: teamsLoading, isFetching: teamsFetching } = Query.taskTeams.all();
    const isAssigner = useMemo(() => teams.some(team => team.Assigners?.some(assigner => assigner.id === loggedInUser?.id)), [teams, loggedInUser?.id]);
    // Dashboard scope: every team where the caller is an explicit assigner —
    // never derived from the loaded 30-day coverage rows.
    const assignerTeams = useMemo(() => teams.filter(team => team.Assigners?.some(assigner => assigner.id === loggedInUser?.id)), [teams, loggedInUser?.id]);
    const assignerTeamIDs = useMemo(() => assignerTeams.map(team => team.id).filter(Boolean), [assignerTeams]);
    const assignerTeamNames = useMemo(() => assignerTeams.map(team => team.name).filter(Boolean), [assignerTeams]);
    const { allowed: canAutoSchedule, reason: autoScheduleReason } = useAutoScheduleAccess();
    const [autoScheduleOpen, setAutoScheduleOpen] = useState(false);
    const { data: coverageTasks = [], isLoading: coverageLoading, isFetching: coverageFetching } = Query.tasks.upcomingTaskAssignments({
        personID: loggedInUser?.id,
        horizonDays: 30,
        options: { enabled: !!loggedInUser?.id && isAssigner },
    });

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => loggedInUser?.can?.('Create Tasks') ? (
                <DropdownMenu
                    testID="TaskDashboardHeaderMenu"
                    options={[
                        { name: 'Create Task', icon: dropdownIcons.add, onSelect: () => router.push('/tasks/create') },
                        { name: 'Create Team', icon: dropdownIcons.people, onSelect: () => router.push('/tasks/teams/create') },
                        { name: 'Schedule Tasks', icon: dropdownIcons.calendar, onSelect: () => router.push('/tasks/schedule') },
                    ]}
                />
            ) : null,
        });
    }, [loggedInUser, navigation, router]);

    const myTeams = useMemo(() => teams.filter(team => team.Assigners?.some(assigner => assigner.id === loggedInUser?.id) || team.Members?.some(member => member.id === loggedInUser?.id)), [loggedInUser?.id, teams]);
    const sortedPersonalTasks = useMemo(() => [...personalTasks].sort((left, right) => new Date(getTaskInstanceDate(left) || 0) - new Date(getTaskInstanceDate(right) || 0)), [personalTasks]);
    const awaitingResponse = useMemo(() => personalTasks.filter(task => {
        const assignment = task.Assignees?.find(assignee => assignee?.id === loggedInUser?.id);
        return assignment && (!assignment.assignmentResponse || assignment.assignmentResponse === 'NO_RESPONSE');
    }).length, [loggedInUser?.id, personalTasks]);
    const overdue = useMemo(() => personalTasks.filter(task => {
        const timestamp = new Date(getTaskInstanceDate(task)).getTime();
        return !Number.isNaN(timestamp) && !task.completed && !task.Task?.assumeCompleted && timestamp < Date.now();
    }).length, [personalTasks]);
    const openSlots = useMemo(() => coverageTasks.reduce((total, task) => total + Math.max(0, getCoverageNeed(task) - getActiveAssigneeCount(task)), 0), [coverageTasks]);
    const gapCount = useMemo(() => coverageTasks.filter(hasCoverageGap).length, [coverageTasks]);

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInUser?.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInUser?.id) });
        queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.all() });
    }, [loggedInUser?.id, queryClient]);

    const loading = userLoading || personalLoading || coverageLoading || teamsLoading;
    const refreshing = !loading && (personalFetching || coverageFetching || teamsFetching);

    if (loading) {
        return <View style={styles.loading}><ActivityIndicator size="large" /></View>;
    }

    return (
        <ScrollView
            style={styles.root}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}
        >
            <View style={styles.hero}>
                <View style={styles.heroCopy}>
                    <Text style={styles.heroEyebrow}>NEXT 30 DAYS</Text>
                    <Text style={styles.heroTitle}>Tasks</Text>
                    <Text style={styles.heroText}>Staff coverage, respond to your work, and team operations.</Text>
                </View>
                {loggedInUser?.can?.('Create Tasks') && (
                    <View style={styles.heroActions}>
                        <SolidButton testID="TaskDashboardCreateTask" text="Create task" iconName="add" iconBefore onPress={() => router.push('/tasks/create')} />
                        <SolidButton testID="TaskDashboardCreateTeam" text="Create team" iconName="people-outline" iconBefore variant="muted" onPress={() => router.push('/tasks/teams/create')} />
                    </View>
                )}
            </View>

            <View style={styles.attentionStrip}>
                <Text style={styles.attentionTitle}>Needs attention</Text>
                <View style={styles.attentionItems}>
                    <AttentionItem icon="chatbox-ellipses-outline" value={awaitingResponse} label="awaiting response" tone={awaitingResponse > 0 ? 'warning' : 'tasks'} />
                    {isAssigner && <AttentionItem icon="person-add-outline" value={openSlots} label="open slots" tone={openSlots > 0 ? 'warning' : 'tasks'} />}
                    <AttentionItem icon="alert-circle-outline" value={overdue} label="overdue" tone={overdue > 0 ? 'error' : 'tasks'} />
                </View>
            </View>

            <View style={styles.columns(desktop)}>
                {isAssigner && (
                    <View style={styles.primaryColumn(desktop)}>
                        <SectionHeader
                            eyebrow="COVERAGE BOARD"
                            title="Managed coverage"
                            actionLabel="All teams"
                            testID="TaskDashboardAllTeams"
                            onAction={() => router.push('/tasks/teams')}
                            extraAction={{
                                label: 'Auto-Schedule',
                                icon: 'sparkles',
                                testID: 'TaskDashboardAutoSchedule',
                                disabled: !canAutoSchedule,
                                hint: autoScheduleReason,
                                onPress: () => setAutoScheduleOpen(true),
                            }}
                        />
                        <View style={styles.coverageSummary}>
                            <Text style={styles.coverageSummaryText}>{gapCount > 0 ? `${gapCount} assignment${gapCount === 1 ? '' : 's'} need staffing` : 'No coverage gaps detected'}</Text>
                            <Text style={styles.coverageSummaryMeta}>Explicit team assignments only</Text>
                        </View>
                        <CoverageBoard taskInstances={coverageTasks} fromScreen="/tasks" />
                    </View>
                )}

                <View style={styles.secondaryColumn(desktop, !isAssigner)}>
                    <View>
                        <SectionHeader eyebrow="PERSONAL" title="My tasks" actionLabel="My tasks" testID="TaskDashboardMyTasks" onAction={() => router.push({ pathname: `/people/${loggedInUser?.id}`, params: { tab: 'tasks' } })} />
                        <View style={styles.taskPreview}>
                            {sortedPersonalTasks.length === 0 ? (
                                <View style={styles.compactEmpty}>
                                    <Unicon name="checkmark-circle-outline" size={20} colorVariant="success" />
                                    <Text style={styles.compactEmptyText}>No personal tasks due.</Text>
                                </View>
                            ) : sortedPersonalTasks.slice(0, 5).map(task => <TaskCard key={task.id} taskInstance={task} showEvent fromScreen="/tasks" />)}
                        </View>
                    </View>

                    {myTeams.length > 0 && (
                        <View style={styles.myTeamsSection}>
                            <SectionHeader eyebrow="OPERATIONS" title="My teams" actionLabel="View all" testID="TaskDashboardMyTeamsAll" onAction={() => router.push('/tasks/teams')} />
                            <View style={styles.teamList}>{myTeams.slice(0, 5).map(team => <TeamPreviewCard key={team.id} team={team} isTeamAssigner={team.Assigners?.some(assigner => assigner.id === loggedInUser?.id)} />)}</View>
                        </View>
                    )}

                    <View style={styles.browseCard}>
                        <Text style={styles.browseEyebrow}>BROWSE</Text>
                        <TouchableOpacity testID="TaskDashboardBrowseLibrary" accessibilityRole="button" accessibilityLabel="Open task library" style={styles.browseLink} onPress={() => router.push('/tasks/library')}>
                            <View><Text style={styles.browseTitle}>Task library</Text><Text style={styles.browseText}>Definitions and individual tasks</Text></View>
                            <Unicon name="arrow-forward" size={18} colorVariant="tasks" />
                        </TouchableOpacity>
                        <TouchableOpacity testID="TaskDashboardBrowseTeams" accessibilityRole="button" accessibilityLabel="Open all teams" style={styles.browseLink} onPress={() => router.push('/tasks/teams')}>
                            <View><Text style={styles.browseTitle}>All teams</Text><Text style={styles.browseText}>Team setup and operations</Text></View>
                            <Unicon name="arrow-forward" size={18} colorVariant="tasks" />
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
            <AutoScheduleModal
                isOpen={autoScheduleOpen}
                onClose={() => setAutoScheduleOpen(false)}
                teamIDs={assignerTeamIDs}
                teamNames={assignerTeamNames}
                eventID={null}
                eventName={null}
                source="managed-coverage"
            />
        </ScrollView>
    );
}

const styles = StyleSheet.create(theme => ({
    root: { flex: 1, backgroundColor: 'transparent' },
    loading: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' },
    content: { width: '100%', maxWidth: 1240, alignSelf: 'center', paddingHorizontal: 16, paddingTop: 18, paddingBottom: 110, gap: 16 },
    hero: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, paddingVertical: 6 },
    heroCopy: { flex: 1, minWidth: 260, maxWidth: 650 },
    heroEyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 11, letterSpacing: 1.2 },
    heroTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 28, lineHeight: 34, marginTop: 5 },
    heroText: { color: theme.colors.textSecondary, fontSize: 14, lineHeight: 20, marginTop: 5 },
    heroActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    attentionStrip: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14, backgroundColor: theme.colors.surface, borderRadius: 14, paddingHorizontal: 16, paddingVertical: 11, boxShadow: theme.shadows.card },
    attentionTitle: { color: theme.colors.text, fontFamily: theme.fonts.bold, fontSize: 13, marginRight: 4 },
    attentionItems: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16 },
    attentionItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    attentionIcon: tone => ({ width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors[`${tone}Container`] || theme.colors.tasksContainer }),
    attentionValue: { color: theme.colors.text, fontFamily: theme.fonts.bold, fontSize: 15 },
    attentionLabel: { color: theme.colors.textSecondary, fontSize: 12 },
    columns: desktop => ({ flexDirection: desktop ? 'row' : 'column-reverse', alignItems: 'flex-start', gap: 22 }),
    primaryColumn: desktop => ({ width: desktop ? undefined : '100%', flex: desktop ? 1.35 : undefined, minWidth: 0 }),
    secondaryColumn: (desktop, isPrimary = false) => ({ width: desktop ? undefined : '100%', flex: desktop ? (isPrimary ? 1.35 : 0.85) : undefined, minWidth: 0, gap: 22 }),
    sectionHeaderWrap: { marginBottom: 10 },
    sectionHeader: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 },
    sectionHeading: { flex: 1 },
    eyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1 },
    sectionTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 19, marginTop: 2 },
    sectionAction: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 5 },
    sectionActionText: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 12 },
    sectionActionTextDisabled: { color: theme.colors.textTertiary },
    sectionActionHint: { color: theme.colors.textSecondary, fontSize: 11, marginTop: 3, paddingRight: 4 },
    coverageSummary: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: 6, paddingHorizontal: 5, marginBottom: 10 },
    coverageSummaryText: { color: theme.colors.textSecondary, fontSize: 12 },
    coverageSummaryMeta: { color: theme.colors.textTertiary, fontSize: 11 },
    taskPreview: { gap: 2 },
    compactEmpty: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: theme.colors.surface, padding: 16, borderRadius: 14, boxShadow: theme.shadows.card },
    compactEmptyText: { color: theme.colors.textSecondary, fontSize: 13 },
    myTeamsSection: { marginTop: 2 },
    teamList: { gap: 8 },
    teamCard: { boxShadow: theme.shadows.card },
    teamCardContent: { flexDirection: 'row', alignItems: 'center', gap: 11, padding: 12 },
    teamRoleBadge: isAssigner => ({ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: isAssigner ? theme.colors.tasksContainer : theme.colors.surfaceVariant, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 7 }),
    teamRoleBadgeText: isAssigner => ({ color: isAssigner ? theme.colors.tasks : theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 10 }),
    teamIcon: { width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.tasksContainer },
    teamCopy: { flex: 1 },
    teamName: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 14 },
    teamMeta: { color: theme.colors.textSecondary, fontSize: 11, marginTop: 2 },
    browseCard: { backgroundColor: theme.colors.tasksContainer, borderRadius: 14, padding: 14, gap: 4 },
    browseEyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1, marginBottom: 3 },
    browseLink: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingVertical: 9 },
    browseTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 14 },
    browseText: { color: theme.colors.textSecondary, fontSize: 11, marginTop: 2 },
}));
